// An expired question in a session's transcript (variant A-5, Poulpe-608).
// Once its expiry has passed, its agent waits no more and has gone on, so
// the question leaves the dock above the composer. It is still open and
// still takes an answer, and it shows where it was asked:
//
// 1. after the transcript's block that holds the agent's `ask` call whose
//    output names the question: MCP's `ask` gives back `{"handle": <id>}`
//    (crates/poulpe-server/src/mcp/tools.rs);
// 2. else after the last block at or before the time it was asked, from
//    its issue's record (asks.ts, `askedAt`);
// 3. else at the transcript's end.
//
// Where it shows is only a place: what the call's output says decides
// nothing else. As more of the transcript arrives, the question moves to
// its call once that is read.
//
// A question answered after its expiry stays in its place, as answered,
// while the board holds it (store.ts, `inPlay`); a cancelled one goes, and
// the answer the transcript keeps where it came in stays (asks.ts).
import type { Question } from '../api/types'
import type { Block, Row } from '../transcript/items'
import type { Item } from '../transcript/types'
import { atOrBefore } from './asks'
import { expired } from './waiting'

/** Where the questions whose call and time are both unknown go: after the transcript's last block. */
export const END = -1

/** The board's `ask` tool, as the transcript names it (transcript/items.ts, `BOARD_TOOL`). */
const ASK_TOOL = /^mcp__poulpe(?:_next)?__ask$/

/** Whether `item` is the `ask` call that gave back question `id`'s handle. */
function asks(item: Item, id: string): boolean {
  if (item.kind !== 'tool' || !ASK_TOOL.test(item.name) || !item.output) return false
  try {
    const said: unknown = JSON.parse(item.output.text)
    return typeof said === 'object' && said !== null && (said as { handle?: unknown }).handle === id
  } catch {
    return false
  }
}

/** A block's items, those of its rows' children included. */
function itemsOf(block: Block): Item[] {
  const out: Item[] = []
  const walk = (row: Row) => {
    out.push(row.item)
    for (const child of row.children) walk(child)
  }
  for (const row of block.kind === 'group' ? block.rows : [block.row]) walk(row)
  return out
}

/**
 * The questions to show after each block, by the block's index, each list
 * in the order given; `END` for those placed after the last block. `askedAt`
 * says when a question was asked, when its record says.
 */
export function placeExpired(blocks: readonly Block[], questions: readonly Question[], askedAt: (id: string) => string | null): Map<number, Question[]> {
  const placed = new Map<number, Question[]>()
  const items = blocks.map(itemsOf)
  for (const question of questions) {
    let at = items.findIndex((list) => list.some((item) => asks(item, question.id)))
    if (at < 0) {
      const asked = askedAt(question.id)
      if (asked !== null) {
        for (let i = 0; i < items.length; i++) if (atOrBefore(items[i]![0]!.at, asked)) at = i
      }
    }
    const key = at < 0 ? END : at
    const list = placed.get(key)
    if (list) list.push(question)
    else placed.set(key, [question])
  }
  return placed
}

/**
 * The questions of `session` the transcript shows as expired at `now`, in
 * the order of their ids: those open with their expiry passed, and those
 * answered after it that the board still holds.
 */
export function expiredOf(questions: Iterable<Question>, session: string, now: number): Question[] {
  return [...questions]
    .filter((question) => {
      if (question.session !== session || !question.expiry || !expired(question, now)) return false
      if (question.state === 'open') return true
      return question.state === 'answered' && !!question.answer && atOrBefore(question.expiry.at, question.answer.at)
    })
    .sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0))
}

/** The id of the element that shows question `id` in the transcript: where the dock's Show goes. */
export const expiredItemId = (id: string) => `expired-question-${id}`
